import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import {
  Inbox, RefreshCw, Loader2, DollarSign, Clock as ClockIcon,
  CheckCircle2, XCircle, MessageSquare, User as UserIcon, MapPin,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABEL = {
  note: 'Note',
  detention: 'Detention',
  tonu: 'TONU',
  layover: 'Layover',
  breakdown: 'Breakdown',
  lumper_reimbursement: 'Lumper',
  toll_reimbursement: 'Toll',
  fuel_advance: 'Fuel Advance',
  cash_advance: 'Cash Advance',
  rate_issue: 'Rate Issue',
  appointment_change: 'Appt Change',
  load_cancellation: 'Load Cancellation',
  other: 'Other',
};

const KIND_COLOR = {
  note:                   'bg-slate-100 text-slate-700 border-slate-200',
  detention:              'bg-amber-100 text-amber-800 border-amber-200',
  tonu:                   'bg-orange-100 text-orange-800 border-orange-200',
  layover:                'bg-amber-100 text-amber-800 border-amber-200',
  breakdown:              'bg-red-100 text-red-800 border-red-200',
  lumper_reimbursement:   'bg-blue-100 text-blue-800 border-blue-200',
  toll_reimbursement:     'bg-blue-100 text-blue-800 border-blue-200',
  fuel_advance:           'bg-emerald-100 text-emerald-800 border-emerald-200',
  cash_advance:           'bg-emerald-100 text-emerald-800 border-emerald-200',
  rate_issue:             'bg-purple-100 text-purple-800 border-purple-200',
  appointment_change:     'bg-purple-100 text-purple-800 border-purple-200',
  load_cancellation:      'bg-red-100 text-red-800 border-red-200',
  other:                  'bg-slate-100 text-slate-700 border-slate-200',
};

const STATUS_META = {
  open:     { label: 'Open',     dot: 'bg-blue-500'   },
  approved: { label: 'Approved', dot: 'bg-emerald-500'},
  rejected: { label: 'Rejected', dot: 'bg-red-500'    },
  reviewed: { label: 'Reviewed', dot: 'bg-slate-500'  },
};

const fmtMoney = (v) => (v == null ? null : `$${Number(v).toFixed(2)}`);
const fmtHours = (v) => (v == null ? null : `${Number(v).toFixed(2)}h`);
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const DriverRequestsInbox = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('open');
  const [filterKind, setFilterKind] = useState('all');
  const [reviewing, setReviewing] = useState(null);
  const [reviewNote, setReviewNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/driver-requests`, { withCredentials: true });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (filterKind !== 'all' && r.kind !== filterKind) return false;
    return true;
  }), [rows, filterStatus, filterKind]);

  const counts = useMemo(() => ({
    all: rows.length,
    open: rows.filter((r) => r.status === 'open').length,
    approved: rows.filter((r) => r.status === 'approved').length,
    rejected: rows.filter((r) => r.status === 'rejected').length,
  }), [rows]);

  const review = async (row, status) => {
    setReviewing(row.id);
    try {
      await axios.post(
        `${API}/driver-requests/${row.id}/review`,
        { status, review_note: reviewNote.trim() || null },
        { withCredentials: true }
      );
      toast.success(status === 'approved' ? 'Request approved' : 'Request rejected');
      setReviewNote('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Review failed');
    } finally {
      setReviewing(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8" data-testid="driver-requests-inbox-page">
        <header className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Inbox className="w-7 h-7 text-[#002FA7]" />
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Driver Requests
              </h1>
            </div>
            <p className="text-slate-600 text-sm">Detention, TONU, lumper, toll, and other driver requests. Review from anywhere.</p>
          </div>
          <button
            data-testid="driver-requests-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </header>

        {/* Status filter chips */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="dr-status-filter">
          {[
            { key: 'open', label: `Open (${counts.open})` },
            { key: 'approved', label: `Approved (${counts.approved})` },
            { key: 'rejected', label: `Rejected (${counts.rejected})` },
            { key: 'all', label: `All (${counts.all})` },
          ].map((s) => (
            <button
              key={s.key}
              onClick={() => setFilterStatus(s.key)}
              data-testid={`dr-filter-${s.key}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${
                filterStatus === s.key
                  ? 'bg-[#002FA7] text-white border-[#002FA7]'
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
            >
              {s.label}
            </button>
          ))}
          <select
            value={filterKind}
            onChange={(e) => setFilterKind(e.target.value)}
            data-testid="dr-kind-filter"
            className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          >
            <option value="all">All kinds</option>
            {Object.entries(KIND_LABEL).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500" data-testid="dr-loading">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500" data-testid="dr-empty">
              No requests match this view.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((row) => {
                const kmeta = KIND_COLOR[row.kind] || KIND_COLOR.other;
                const smeta = STATUS_META[row.status] || STATUS_META.open;
                return (
                  <li key={row.id} className="p-4 md:p-5" data-testid={`dr-row-${row.id}`}>
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border ${kmeta}`}>
                            {KIND_LABEL[row.kind] || row.kind}
                          </span>
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                            <span className={`w-2 h-2 rounded-full ${smeta.dot}`} />
                            {smeta.label}
                          </span>
                          {row.amount_usd != null && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                              <DollarSign className="w-3.5 h-3.5" />
                              {fmtMoney(row.amount_usd)}
                            </span>
                          )}
                          {row.hours != null && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800">
                              <ClockIcon className="w-3.5 h-3.5" />
                              {fmtHours(row.hours)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                          <UserIcon className="w-3.5 h-3.5" />
                          <span className="font-semibold text-slate-900">{row.driver_name || '—'}</span>
                          <span>•</span>
                          <span>{fmtTime(row.created_at)}</span>
                        </div>
                        {row.body && (
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{row.body}</p>
                        )}
                        {row.gps && row.gps.lat != null && row.gps.lng != null && (
                          <a
                            href={`https://www.google.com/maps?q=${row.gps.lat},${row.gps.lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`dr-gps-${row.id}`}
                            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline"
                          >
                            <MapPin className="w-3.5 h-3.5" />
                            {row.gps.lat.toFixed(5)}, {row.gps.lng.toFixed(5)}
                            {row.gps.accuracy_m != null && (
                              <span className="text-slate-400 font-normal">
                                &nbsp;(±{Math.round(row.gps.accuracy_m)}m)
                              </span>
                            )}
                            {row.gps.captured_at && (
                              <span className="text-slate-400 font-normal">
                                &nbsp;· captured {new Date(row.gps.captured_at).toLocaleString()}
                              </span>
                            )}
                          </a>
                        )}
                        {row.status !== 'open' && row.review_note && (
                          <div className="mt-2 text-xs text-slate-600 border-l-2 border-slate-200 pl-3">
                            <MessageSquare className="w-3 h-3 inline mr-1" />
                            <span className="italic">{row.review_note}</span>
                            <span className="text-slate-400"> — {row.reviewer_name} on {fmtTime(row.reviewed_at)}</span>
                          </div>
                        )}
                      </div>

                      {row.status === 'open' && (
                        <div className="flex flex-col gap-2 md:min-w-[220px]">
                          <input
                            data-testid={`dr-review-note-${row.id}`}
                            type="text"
                            placeholder="Optional note (approval / reason)"
                            value={reviewing === row.id ? reviewNote : ''}
                            onFocus={() => setReviewing(row.id)}
                            onChange={(e) => setReviewNote(e.target.value)}
                            className="text-xs px-2 py-1.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                          />
                          <div className="flex gap-2">
                            <button
                              data-testid={`dr-approve-${row.id}`}
                              onClick={() => review(row, 'approved')}
                              disabled={reviewing && reviewing !== row.id}
                              className="flex-1 inline-flex items-center justify-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-md px-3 py-1.5 disabled:opacity-50"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                            </button>
                            <button
                              data-testid={`dr-reject-${row.id}`}
                              onClick={() => review(row, 'rejected')}
                              disabled={reviewing && reviewing !== row.id}
                              className="flex-1 inline-flex items-center justify-center gap-1 bg-white hover:bg-red-50 text-red-600 border border-red-300 text-xs font-semibold rounded-md px-3 py-1.5 disabled:opacity-50"
                            >
                              <XCircle className="w-3.5 h-3.5" /> Reject
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DriverRequestsInbox;
